import {
  Title,
  TextInput,
  Button,
  Container,
  Textarea,
  Stack,
  Select,
  Checkbox,
  Text,
  Group,
  Paper,
  ActionIcon,
  NumberInput,
  Alert,
} from "@mantine/core";
import { IconTrash, IconInfoCircle } from "@tabler/icons-react";
import { useForm } from "@mantine/form";
import { useLocation } from "wouter";
import { useEffect, useState } from "react";
import useToolStore, { headersListToDict } from "./toolStore";
import { getFlows } from "../api";
import JsonSchemaImport from "./JsonSchemaImport";

export default function ToolCreate() {
  const [, setLocation] = useLocation();
  const { createTool } = useToolStore();
  const [flows, setFlows] = useState([]);

  useEffect(() => {
    getFlows().then(setFlows);
  }, []);

  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      name: "",
      description: "",
      parameters: { properties: [], type: "object" },
      headers: [],
      toolType: "external",
      virtualToolId: "",
      agentTemplateId: "",
      maxAgentDepth: 5,
      producerFlowId: "",
      criticFlowId: "",
      maxLoopIterations: 3,
    },
    validate: {
      //emailAddress: (val) => (/^\S+@\S+$/.test(val) ? null : "Invalid email"),
      //tenantSlug: (val) => (/^[a-z][a-z0-9_-]+$/.test(val) ? null : "Invalid slug"),
      // Absolute = the tool names its own host; relative = caller-hosted, origin supplied per
      // request via X-Shuttle-Callback-Url.
      webUrl: (value, values) =>
        values.toolType !== "external" || !value ||
        /^https?:\/\//.test(value) || value.startsWith("/")
          ? null
          : "Use a full URL (https://…) or a caller-hosted path starting with /",
    },
  });

  const handleCreateTool = (values) => {
    // Assuming createTool is a function that sends the tool data to the server
    createTool({
      ...values,
      headers: headersListToDict(values.headers),
    }).then(() => {
      setLocation("/tools");
    });
  };

  const flowOptions = flows.map(f => ({ value: f.id, label: f.name }));
  const virtualToolOptions = [
    { value: "web_search", label: "Web Search" },
    { value: "image_generation", label: "Image Generation" },
    { value: "code_interpreter", label: "Code Interpreter" },
  ];

  const toolTypeOptions = [
    { value: "external", label: "External", description: "Calls your HTTP API when the LLM invokes this tool" },
    { value: "virtual", label: "Virtual", description: "Built-in LLM capability provided by the model vendor" },
    { value: "agent", label: "Agent", description: "Delegates to another Flow as a sub-agent" },
    { value: "critiqueloop", label: "Critique Loop", description: "Iteratively refines output using producer + critic flows" },
    { value: "mcp", label: "MCP", description: "Calls a tool on an external MCP server" },
  ];

  const toolTypeDescriptions = {
    external: "When the LLM calls this tool, PromptShuttle will forward the request to your HTTP endpoint and return the response.",
    virtual: "Virtual tools are built-in capabilities provided by the LLM vendor (e.g. web search, code execution). No external endpoint needed.",
    agent: "Agent tools invoke another Flow as a sub-agent, allowing multi-step LLM orchestration and recursive processing.",
    critiqueloop: "Critique Loop tools run a produce-then-critique cycle: a producer generates output, a critic evaluates it, and the loop repeats until the critic approves or max iterations are reached.",
    mcp: "MCP tools call external MCP (Model Context Protocol) servers. They are typically imported from an MCP server rather than created manually. Go to MCP Servers to add a server and import tools.",
  };

  return (
    <Container size="sm">
      <Title order={1} mb="md">
        Create Tool
      </Title>

      <form onSubmit={form.onSubmit(handleCreateTool)}>
        <Stack>
          <Group grow>
            <TextInput
              label="Name"
              placeholder="get_current_price"
              {...form.getInputProps("name")}
              radius="md"
            />
            <Select
              label="Tool Type"
              data={toolTypeOptions}
              {...form.getInputProps("toolType")}
              radius="md"
            />
          </Group>

          <Textarea
            label="Description"
            autosize
            minRows={1}
            maxRows={3}
            placeholder="Retrieves the current price of a stock"
            {...form.getInputProps("description")}
            radius="md"
          />

          {toolTypeDescriptions[form.values.toolType] && (
            <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
              {toolTypeDescriptions[form.values.toolType]}
            </Alert>
          )}

          {form.values.toolType === "external" && (
            <>
              <TextInput
                label="Remote URL"
                placeholder={`https://yourapi.example.com/foo`}
                description="Invoked with a GET request, arguments appended as query string. A full URL (https://…) points at a fixed host. A path starting with / makes this a caller-hosted tool: the host comes from the calling app's X-Shuttle-Callback-Url header on every request, so one definition serves both a developer's machine and production."
                {...form.getInputProps(`webUrl`)}
                radius="md"
              />

              {/* Headers editor */}
              <Group mt="md" justify="space-between" align="center">
                <Text fw={500}>HTTP Headers</Text>
                <Button
                  variant="outline"
                  onClick={() =>
                    form.insertListItem("headers", { key: "", value: "" })
                  }
                >
                  Add Header
                </Button>
              </Group>
              {form.values.headers?.map((_, idx) => (
                <Group key={idx} mb="xs" align="flex-end">
                  <TextInput
                    label="Header"
                    placeholder="Authorization"
                    {...form.getInputProps(`headers.${idx}.key`)}
                    radius="md"
                    style={{ flex: 1 }}
                  />
                  <TextInput
                    label="Value"
                    placeholder="Bearer your-token-here"
                    {...form.getInputProps(`headers.${idx}.value`)}
                    radius="md"
                    style={{ flex: 2 }}
                  />
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    mb={4}
                    onClick={() => form.removeListItem("headers", idx)}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              ))}
            </>
          )}

          {form.values.toolType === "virtual" && (
            <Select
              label="Virtual Tool"
              description="Select a built-in capability provided by the LLM vendor"
              data={virtualToolOptions}
              {...form.getInputProps("virtualToolId")}
              radius="md"
            />
          )}

          {form.values.toolType === "agent" && (
            <Group grow align="flex-end">
              <Select
                label="Agent Flow"
                description="Select the flow that will be invoked as a sub-agent"
                data={flowOptions}
                searchable
                {...form.getInputProps("agentTemplateId")}
                radius="md"
              />
              <NumberInput
                label="Max Depth"
                description="Maximum recursion depth"
                min={1}
                max={10}
                {...form.getInputProps("maxAgentDepth")}
                radius="md"
              />
            </Group>
          )}

          {form.values.toolType === "critiqueloop" && (
            <Stack>
              <Group grow align="flex-end">
                <Select
                  label="Producer Flow"
                  description="The flow that generates output to be refined"
                  data={flowOptions}
                  searchable
                  {...form.getInputProps("producerFlowId")}
                  radius="md"
                />
                <Select
                  label="Critic Flow"
                  description="The flow that evaluates output. Must return JSON with {approved: bool, feedback: string}"
                  data={flowOptions}
                  searchable
                  {...form.getInputProps("criticFlowId")}
                  radius="md"
                />
              </Group>
              <NumberInput
                label="Max Iterations"
                description="Maximum number of produce-critique cycles before returning the best output"
                min={1}
                max={10}
                {...form.getInputProps("maxLoopIterations")}
                radius="md"
                style={{ maxWidth: 200 }}
              />
            </Stack>
          )}

          {form.values.parameters.properties.map((param, index) => (
            <Paper withBorder p="xs" mb="lg" key={index}>
              <Stack gap="xs">
                <Group justify="space-between" align="center">
                  <Text>{`Parameter ${index + 1}`}</Text>
                  <Button
                    variant="subtle"
                    color="red"
                    onClick={() =>
                      form.removeListItem("parameters.properties", index)
                    }
                  >
                    Remove
                  </Button>
                </Group>
                <Group align="center">
                  <TextInput
                    style={{ flex: 1 }}
                    label="Name"
                    placeholder={`Parameter ${index + 1} name`}
                    {...form.getInputProps(
                      `parameters.properties.${index}.name`
                    )}
                    radius="md"
                  />
                  <Checkbox
                    pt="lg"
                    label="Required"
                    {...form.getInputProps(
                      `parameters.properties.${index}.required`,
                      { type: "checkbox" }
                    )}
                  />
                </Group>
                <TextInput
                  label="Description"
                  placeholder={`Parameter ${index + 1} description`}
                  {...form.getInputProps(
                    `parameters.properties.${index}.description`
                  )}
                  radius="md"
                />
                <Group>
                  <Select
                    style={{ flex: 1 }}
                    data={["string", "number"]}
                    {...form.getInputProps(
                      `parameters.properties.${index}.type`
                    )}
                    label={`Type`}
                  />
                  <Checkbox
                    pt="lg"
                    label="Is List"
                    {...form.getInputProps(
                      `parameters.properties.${index}.isList`,
                      { type: "checkbox" }
                    )}
                  />
                </Group>
              </Stack>
            </Paper>
          ))}
          <Group>
            <Button
              variant="outline"
              onClick={() =>
                form.insertListItem("parameters.properties", {
                  name: "",
                  description: "",
                  type: "string",
                  required: false,
                })
              }
            >
              Add Property
            </Button>
            <JsonSchemaImport
              onImport={(params) => {
                form.setFieldValue("parameters.properties", params);
              }}
            />
          </Group>

          <Button mt="lg" type="submit">
            Create
          </Button>
        </Stack>
      </form>
    </Container>
  );
}
